import React, { useState } from 'react';
import { toProperCase } from '../utils/textUtils';
import { API_ENDPOINTS, API_BASE_URL } from '../constants/api';
import './OnboardingWizard.css';

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
  organizationName: string;
  userToken: string;
}

interface BusinessUnit {
  business: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  email?: string;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  onComplete,
  onSkip,
  organizationName,
  userToken
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([
    {
      business: organizationName,
      country: 'USA'
    }
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [selectedOption, setSelectedOption] = useState('useCropMaps');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState('');
  const [processingError, setProcessingError] = useState('');
  const [processingResults, setProcessingResults] = useState<any>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [savedBusinessUnits, setSavedBusinessUnits] = useState<any[]>([]);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState<string>('');


  const totalSteps = 4;

  const handleNext = () => {
    if (currentStep === 3) {
      // For John Deere or Case ASF Connect, go to step 4
      if (selectedOption === 'johnDeere' || selectedOption === 'caseAsf') {
        setCurrentStep(4);
        return;
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleComplete = async () => {
    // Save the selected option to localStorage
    localStorage.setItem('onboardingChoice', selectedOption);

    // For options 1 (useCropMaps) and 4 (manualDraw), complete the wizard immediately
    if (selectedOption === 'useCropMaps' || selectedOption === 'manualDraw') {

      // Save business units before completing
      await handleSaveStep3(true);
      // Close the wizard
      onComplete();
    } else if (selectedOption === 'johnDeere' || selectedOption === 'caseAsf') {
      // For John Deere or Case ASF Connect options, save business units first, then go to Page 4

      try {
        await handleSaveStep3(false); // Don't complete the wizard, just save business units
        setCurrentStep(4);
      } catch (error) {
        console.error('Failed to save business units:', error);
        // Don't proceed to step 4 if business units couldn't be saved
        return;
      }
    } else {
      // For other options, we'll handle them differently

      // For now, just save business units and complete
      await handleSaveStep3(true);
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  const updateBusinessUnit = (index: number, field: keyof BusinessUnit, value: string) => {
    const updated = [...businessUnits];

    // Apply proper case conversion to specific fields
    const fieldsToProperCase = ['business', 'address_line1', 'address_line2', 'city'];
    const processedValue = fieldsToProperCase.includes(field) ? toProperCase(value) : value;

    updated[index] = { ...updated[index], [field]: processedValue };
    setBusinessUnits(updated);
  };

  const addBusinessUnit = () => {
    setBusinessUnits([...businessUnits, { business: '', country: 'USA' }]);
  };

  const removeBusinessUnit = (index: number) => {
    if (businessUnits.length > 1) {
      setBusinessUnits(businessUnits.filter((_, i) => i !== index));
    }
  };

  // Helper function to get onboarding choice from localStorage
  const getOnboardingChoice = () => {
    return localStorage.getItem('onboardingChoice');
  };

  // File upload handler
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setProcessingError('Please select a ZIP file containing John Deere Operation Center data.');
      return;
    }
    setUploadedFile(file);
    setProcessingError('');
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  // Process John Deere file
  const handleProcessImport = async () => {
    if (!uploadedFile) return;

    setIsProcessing(true);
    setProcessingError('');
    setProcessingProgress('Starting import process...');

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      // Add the selected business unit ID to the import request
      if (selectedBusinessUnitId) {
        formData.append('business_unit_id', selectedBusinessUnitId);
      }

      // Use the same endpoint for both John Deere and Case ASF Connect since they use the same field names
      const endpoint = '/john-deere/import';

      setProcessingProgress('Uploading file to server...');

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorData}`);
      }

      setProcessingProgress('Processing completed successfully!');
      const results = await response.json();
      setProcessingResults(results);

      // Close wizard after a short delay
      setTimeout(() => {
        onComplete();
      }, 2000);

    } catch (error) {
      console.error('Import error:', error);
      setProcessingError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsProcessing(false);
    }
  };


  const handleSaveStep3 = async (shouldComplete: boolean = true) => {
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      // Validate that at least one business unit has a name
      const hasValidBusiness = businessUnits.some(unit => unit.business.trim() !== '');
      if (!hasValidBusiness) {
        throw new Error('At least one business unit must have a name');
      }

      // Save all business units to the API
      const promises = businessUnits.map(async (unit) => {
        // Skip empty business units
        if (!unit.business.trim()) return null;

        const response = await fetch(API_ENDPOINTS.BUSINESS_UNITS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userToken}`,
          },
          body: JSON.stringify(unit),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to save business unit');
        }

        return response.json();
      });

      const savedUnits = await Promise.all(promises.filter(Boolean));
      setSavedBusinessUnits(savedUnits);

      // Set the first business unit as selected by default
      if (savedUnits.length > 0) {
        setSelectedBusinessUnitId(savedUnits[0].business_id);
      }

      // Only complete the wizard if shouldComplete is true
      if (shouldComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error saving business units:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save business units');
      throw error; // Re-throw so calling function can handle it
    } finally {
      setIsSaving(false);
    }
  };

  const renderProgressBar = () => (
    <div className="progress-bar">
      <div className="progress-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }}></div>
    </div>
  );

  const renderStep1 = () => (
    <div className="step-content">
      <h2>Welcome to RAVAH|CARBON!</h2>
      <p>Let's set up your farm business information. The program is designed to enable you to have multiple business units (if applicable).</p>

      <div className="info-box">
        <h3>What is a Business Unit?</h3>
        <p>A Business Unit represents a distinct operational entity within your organization. This could be:</p>
        <ul>
          <li>A different business entity</li>
          <li>A distinct operational division</li>
        </ul>
      </div>

      <div className="button-group">
        <button onClick={handleSkip} className="btn-secondary">Skip for now</button>
        <button onClick={handleNext} className="btn-primary">Let's get started</button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <h2>Set up your Business Unit(s)</h2>
      <p>Let's configure your business units.</p>

      {saveError && (
        <div className="error-message">
          <p>Error: {saveError}</p>
        </div>
      )}



      {businessUnits.map((unit, index) => (
        <div key={index} className="business-unit-form">
          <div className="form-header">
            <h3>Business Unit {index + 1}</h3>
            {businessUnits.length > 1 && (
              <button
                onClick={() => removeBusinessUnit(index)}
                className="btn-remove"
                type="button"
              >
                Remove
              </button>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Business Name *</label>
              <input
                type="text"
                value={unit.business}
                onChange={(e) => updateBusinessUnit(index, 'business', e.target.value)}
                placeholder="Enter business name"
                required
                autoComplete="off"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                value={unit.address_line1 || ''}
                onChange={(e) => updateBusinessUnit(index, 'address_line1', e.target.value)}
                placeholder="Street address"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                value={unit.address_line2 || ''}
                onChange={(e) => updateBusinessUnit(index, 'address_line2', e.target.value)}
                placeholder="Suite, unit, etc."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={unit.city || ''}
                onChange={(e) => updateBusinessUnit(index, 'city', e.target.value)}
                placeholder="City"
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={unit.state || ''}
                onChange={(e) => updateBusinessUnit(index, 'state', e.target.value)}
                placeholder="State"
              />
            </div>
            <div className="form-group">
              <label>Postal Code</label>
              <input
                type="text"
                value={unit.postal_code || ''}
                onChange={(e) => updateBusinessUnit(index, 'postal_code', e.target.value)}
                placeholder="ZIP code"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                value={unit.country || 'USA'}
                onChange={(e) => updateBusinessUnit(index, 'country', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={unit.phone || ''}
                onChange={(e) => updateBusinessUnit(index, 'phone', e.target.value)}
                placeholder="Phone number"
              />
            </div>
          </div>
        </div>
      ))}

      <button onClick={addBusinessUnit} className="btn-secondary" type="button">
        + Add Another Business Unit
      </button>

      <div className="button-group">
        <button onClick={handlePrevious} className="btn-secondary">Previous</button>
        <button onClick={handleNext} className="btn-primary">Next</button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <h2>Almost Done!</h2>
      <p>Great! You've set up {businessUnits.length} business unit{businessUnits.length > 1 ? 's' : ''}. Now let's save your business units and complete the setup.</p>

      {saveError && (
        <div className="error-message">
          <p>Error: {saveError}</p>
        </div>
      )}

      <div className="summary-box">
        <h3>What's Next?</h3>

        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              name="nextStep"
              value="useCropMaps"
              checked={selectedOption === 'useCropMaps'}
              onChange={(e) => setSelectedOption(e.target.value)}
            />
            <span className="radio-label">
              Manually add farm names on our Setup tab, then draw your field boundaries on the map
            </span>
          </label>

          <label className="radio-option">
            <input
              type="radio"
              name="nextStep"
              value="johnDeere"
              checked={selectedOption === 'johnDeere'}
              onChange={(e) => setSelectedOption(e.target.value)}
            />
            <span className="radio-label">
              Import all your farms and fields from a John Deere Operation Center Shape file export
            </span>
          </label>

          <label className="radio-option">
            <input
              type="radio"
              name="nextStep"
              value="caseAsf"
              checked={selectedOption === 'caseAsf'}
              onChange={(e) => setSelectedOption(e.target.value)}
            />
            <span className="radio-label">
              Import all your farms and fields from a Case AFS Connect Shape file export
            </span>
          </label>
        </div>


      </div>

      <div className="button-group">
        <button onClick={handlePrevious} className="btn-secondary">Previous</button>
        <button
          onClick={handleComplete}
          className="btn-primary"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' :
            selectedOption === 'useCropMaps' || selectedOption === 'manualDraw'
              ? 'Save Business Unit(s) & Complete Setup'
              : 'Save Business Unit(s) & Continue'
          }
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const isCaseAsf = selectedOption === 'caseAsf';
    const title = isCaseAsf ? 'Import Case ASF Connect Data' : 'Import John Deere Operation Center Data';
    const description = isCaseAsf
      ? 'Upload your Case ASF Connect export file (ZIP format) to automatically import your farms and fields.'
      : 'Upload your John Deere Operation Center export file (ZIP format) to automatically import your farms and fields.';
    const buttonText = isCaseAsf ? 'Import Farms & Fields' : 'Import Farms & Fields';
    const fileLabel = isCaseAsf ? 'Case ASF Connect ZIP file' : 'John Deere ZIP file';

    return (
      <div className="step-content">
        <h2>{title}</h2>
        <p>{description}</p>
        {savedBusinessUnits.length > 1 && (
          <p className="business-unit-note">
            <strong>Note:</strong> Since you have multiple business units, you'll need to select which one these farms and fields will be associated with.
          </p>
        )}

        {/* Business Unit Selection - Only show if multiple business units */}
        {savedBusinessUnits.length > 1 && (
          <div className="business-unit-selection">
            <h3>Select Business Unit for Import</h3>
            <p>Choose which business unit these farms and fields will be associated with:</p>
            <select
              value={selectedBusinessUnitId}
              onChange={(e) => setSelectedBusinessUnitId(e.target.value)}
              className="business-unit-dropdown"
            >
              {savedBusinessUnits.map((unit) => (
                <option key={unit.business_id} value={unit.business_id}>
                  {unit.business}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="file-upload-section">
          <div
            className={`file-upload-box ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="importFile"
              accept=".zip"
              onChange={handleFileUpload}
              disabled={isProcessing}
              style={{ display: 'none' }}
            />
            <label htmlFor="importFile" className="file-upload-label">
              {uploadedFile ? (
                <div className="file-selected">
                  <span className="file-name">📁 {uploadedFile.name}</span>
                  <span className="file-size">({(uploadedFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
              ) : (
                <div className="file-upload-placeholder">
                  <span className="upload-icon">📁</span>
                  <span>Click to select {fileLabel}</span>
                  <span className="upload-hint">or drag and drop here</span>
                </div>
              )}
            </label>
          </div>

          {processingError && (
            <div className="error-message">
              <strong>Error:</strong> {processingError}
            </div>
          )}

          {processingProgress && (
            <div className="progress-message">
              {processingProgress}
            </div>
          )}

          {processingResults && (
            <div className="success-message">
              <strong>Import completed successfully!</strong>
              <br />
              Created {processingResults.farmsCreated} farms and {processingResults.fieldsCreated} fields.
            </div>
          )}
        </div>

        <div className="info-box">
          <h4>What happens during import?</h4>
          <ul>
            <li>Your ZIP file is securely uploaded and processed</li>
            <li>Farm and field boundaries are extracted</li>
            <li>Area calculations are performed automatically</li>
            <li>All data is saved to your account</li>
          </ul>
        </div>

        {/* Import Later Option */}
        <div className="import-later-option">
          <h4>Need more time to organize your files?</h4>
          <p>You can import your John Deere or Case ASF Connect data later from the "Setup Farm and Fields" tab. This will give you time to:</p>
          <ul>
            <li>Organize and review your export files</li>
            <li>Ensure all data is accurate before import</li>
            <li>Import data for specific business units when ready</li>
          </ul>
          <button
            onClick={() => onComplete()}
            className="btn-secondary"
            style={{ marginTop: '15px' }}
          >
            Complete Setup & Import Later
          </button>
        </div>

        <div className="button-group">
          <button onClick={handlePrevious} className="btn-secondary" disabled={isProcessing}>
            Previous
          </button>
          <button
            onClick={handleProcessImport}
            className="btn-primary"
            disabled={!uploadedFile || isProcessing}
          >
            {isProcessing ? 'Processing...' : buttonText}
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="onboarding-wizard">
      {renderProgressBar()}
      <div className="wizard-container">
        <div className="step-indicator">
          Step {currentStep} of {totalSteps}
        </div>
        {renderCurrentStep()}
      </div>

    </div>
  );
};

export default OnboardingWizard;
